import React, { createContext, useContext, useReducer, useMemo, useEffect, useState, useCallback } from 'react';
import { 
  subscribeToBeds, 
  subscribeToAlerts,
  assignPatientToBed,
  releaseBed as releaseFirestoreBed,
  updateBedStatus as updateFirestoreBedStatus,
  getBeds,
  getZones,
  createAlert,
  BED_STATUS as FIRESTORE_BED_STATUS
} from '../services/firebase.service';

// Re-export bed status constants for compatibility
export const BED_STATUS = FIRESTORE_BED_STATUS;

// Bed zones configuration (matching Firestore data)
export const BED_ZONES = {
  EMERGENCY: { 
    id: 'emergency',
    name: 'Emergency Department',
    capacity: 50,
    color: '#dc2626', // red-600
    esiLevels: [1, 2, 3, 4, 5],
    description: 'Emergency room beds for acute care'
  },
  ICU: {
    id: 'icu',
    name: 'Intensive Care Unit',
    capacity: 20,
    color: '#ea580c', // orange-600
    esiLevels: [1, 2],
    description: 'Critical care beds with advanced monitoring'
  },
  GENERAL: {
    id: 'general',
    name: 'General Ward',
    capacity: 100,
    color: '#2563eb', // blue-600
    esiLevels: [2, 3, 4],
    description: 'Standard hospital beds'
  },
  PEDIATRIC: {
    id: 'pediatric',
    name: 'Pediatric Ward',
    capacity: 30,
    color: '#16a34a', // green-600
    esiLevels: [1, 2, 3, 4, 5],
    description: 'Beds for pediatric patients'
  },
  OBSERVATION: {
    id: 'observation',
    name: 'Observation Unit',
    capacity: 25,
    color: '#ca8a04', // yellow-600
    esiLevels: [2, 3, 4],
    description: 'Short-stay observation beds'
  }
};

// Reducer actions
const BED_ACTIONS = {
  SET_BEDS: 'SET_BEDS',
  SET_ZONES: 'SET_ZONES',
  SET_ALERTS: 'SET_ALERTS',
  UPDATE_BED: 'UPDATE_BED',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS'
};

// Reducer function
const bedManagementReducer = (state, action) => {
  switch (action.type) {
    case BED_ACTIONS.SET_BEDS:
      return {
        ...state,
        beds: action.payload,
        loading: false
      };
      
    case BED_ACTIONS.SET_ZONES:
      return {
        ...state,
        zones: action.payload
      };
      
    case BED_ACTIONS.SET_ALERTS:
      return {
        ...state,
        alerts: action.payload
      };
      
    case BED_ACTIONS.UPDATE_BED:
      return {
        ...state,
        beds: state.beds.map(bed => 
          bed.id === action.payload.id ? action.payload : bed
        )
      };
      
    case BED_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
      
    case BED_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
      
    case BED_ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload.isConnected,
        connectionError: action.payload.error
      };
      
    default:
      return state;
  }
};

// Context
const BedManagementContext = createContext();

// Provider component
export const BedManagementProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bedManagementReducer, {
    beds: [],
    zones: [],
    alerts: [],
    loading: true,
    error: null,
    isConnected: false,
    connectionError: null
  });
  
  // Firestore subscriptions
  const [bedUnsubscribe, setBedUnsubscribe] = useState(null);
  const [alertUnsubscribe, setAlertUnsubscribe] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Initialize Firestore subscriptions
  useEffect(() => {
    dispatch({ type: BED_ACTIONS.SET_LOADING, payload: true });
    
    // Load initial data
    const loadInitialData = async () => {
      try {
        // Load zones
        const zonesData = await getZones();
        dispatch({ type: BED_ACTIONS.SET_ZONES, payload: zonesData });
        
        // Load initial beds data
        const bedsData = await getBeds();
        dispatch({ type: BED_ACTIONS.SET_BEDS, payload: bedsData });
        
        dispatch({ 
          type: BED_ACTIONS.SET_CONNECTION_STATUS, 
          payload: { isConnected: true, error: null } 
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
        dispatch({ type: BED_ACTIONS.SET_ERROR, payload: error.message });
        dispatch({ 
          type: BED_ACTIONS.SET_CONNECTION_STATUS, 
          payload: { isConnected: false, error: error.message } 
        });
      }
    };
    
    loadInitialData();
    
    // Subscribe to real-time updates
    const unsubscribeBeds = subscribeToBeds((beds) => {
      console.log('🔄 Beds updated from Firestore:', beds.length);
      dispatch({ type: BED_ACTIONS.SET_BEDS, payload: beds });
      setLastUpdate({ type: 'sync', timestamp: new Date() });
    });
    
    const unsubscribeAlerts = subscribeToAlerts((alerts) => {
      console.log('🔔 Alerts updated:', alerts.length);
      dispatch({ type: BED_ACTIONS.SET_ALERTS, payload: alerts });
    });
    
    setBedUnsubscribe(() => unsubscribeBeds);
    setAlertUnsubscribe(() => unsubscribeAlerts);
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeBeds) unsubscribeBeds();
      if (unsubscribeAlerts) unsubscribeAlerts();
    };
  }, []);
  
  // Computed values
  const bedsByZone = useMemo(() => {
    const grouped = {};
    Object.values(BED_ZONES).forEach(zone => {
      grouped[zone.id] = state.beds.filter(bed => bed.zone === zone.id);
    });
    return grouped;
  }, [state.beds]);
  
  const occupancyStats = useMemo(() => {
    const stats = {
      total: state.beds.length,
      occupied: state.beds.filter(b => b.status === BED_STATUS.OCCUPIED).length,
      available: state.beds.filter(b => b.status === BED_STATUS.AVAILABLE).length,
      cleaning: state.beds.filter(b => b.status === BED_STATUS.CLEANING).length,
      maintenance: state.beds.filter(b => b.status === BED_STATUS.MAINTENANCE).length,
      reserved: state.beds.filter(b => b.status === BED_STATUS.RESERVED).length
    };
    stats.occupancyRate = state.beds.length > 0 
      ? ((stats.occupied / stats.total) * 100).toFixed(1)
      : '0.0';
    
    // Zone-specific stats
    stats.byZone = {};
    Object.values(BED_ZONES).forEach(zone => {
      const zoneBeds = state.beds.filter(b => b.zone === zone.id);
      stats.byZone[zone.id] = {
        total: zoneBeds.length,
        occupied: zoneBeds.filter(b => b.status === BED_STATUS.OCCUPIED).length,
        available: zoneBeds.filter(b => b.status === BED_STATUS.AVAILABLE).length,
        occupancyRate: zoneBeds.length > 0 
          ? ((zoneBeds.filter(b => b.status === BED_STATUS.OCCUPIED).length / zoneBeds.length) * 100).toFixed(1)
          : '0.0'
      };
    });
    
    return stats;
  }, [state.beds]);
  
  // Action creators with Firestore integration
  const assignBed = useCallback(async (bedId, patient) => {
    try {
      // Call Firestore service
      await assignPatientToBed(bedId, {
        patientId: patient.id,
        patientName: patient.name || 'Unknown Patient',
        encounterId: patient.encounterId || `enc-${Date.now()}`,
        esiLevel: patient.esiLevel,
        assignedBy: localStorage.getItem('userId') || 'system'
      });
      
      // Create alert for bed assignment
      await createAlert({
        type: 'operational',
        severity: 'info',
        bedId,
        message: `Bed ${bedId} assigned to ${patient.name || 'patient'}`
      });
      
      setLastUpdate({ type: 'assign', bedId, timestamp: new Date() });
      return { success: true };
    } catch (error) {
      console.error('Error assigning bed:', error);
      return { success: false, error: error.message };
    }
  }, []);
  
  const releaseBed = useCallback(async (bedId) => {
    try {
      const userId = localStorage.getItem('userId') || 'system';
      await releaseFirestoreBed(bedId, userId);
      
      // Create alert for bed release
      await createAlert({
        type: 'operational',
        severity: 'info',
        bedId,
        message: `Bed ${bedId} released and requires cleaning`
      });
      
      setLastUpdate({ type: 'release', bedId, timestamp: new Date() });
      return { success: true };
    } catch (error) {
      console.error('Error releasing bed:', error);
      return { success: false, error: error.message };
    }
  }, []);
  
  const updateBedStatus = useCallback(async (bedId, status, notes) => {
    try {
      await updateFirestoreBedStatus(bedId, { status, notes });
      
      // Create alert for status change if significant
      if (status === BED_STATUS.MAINTENANCE) {
        await createAlert({
          type: 'maintenance',
          severity: 'medium',
          bedId,
          message: `Bed ${bedId} requires maintenance: ${notes || 'No details provided'}`
        });
      }
      
      setLastUpdate({ type: 'update', bedId, timestamp: new Date() });
      return { success: true };
    } catch (error) {
      console.error('Error updating bed status:', error);
      return { success: false, error: error.message };
    }
  }, []);
  
  const transferPatient = useCallback(async (fromBedId, toBedId) => {
    try {
      // Get patient from source bed
      const fromBed = state.beds.find(b => b.id === fromBedId);
      if (!fromBed || !fromBed.patientId) {
        throw new Error('No patient in source bed');
      }
      
      // Release source bed
      await releaseBed(fromBedId);
      
      // Assign to destination bed
      await assignBed(toBedId, {
        id: fromBed.patientId,
        name: fromBed.patientName,
        esiLevel: fromBed.esiLevel,
        encounterId: fromBed.encounterId
      });
      
      // Create transfer alert
      await createAlert({
        type: 'operational',
        severity: 'info',
        message: `Patient transferred from bed ${fromBedId} to ${toBedId}`
      });
      
      setLastUpdate({ type: 'transfer', fromBedId, toBedId, timestamp: new Date() });
      return { success: true };
    } catch (error) {
      console.error('Error transferring patient:', error);
      return { success: false, error: error.message };
    }
  }, [state.beds, assignBed, releaseBed]);
  
  // Find available bed for ESI level
  const findAvailableBed = useCallback((esiLevel) => {
    // Find zones that accept this ESI level
    const suitableZones = Object.values(BED_ZONES).filter(zone => 
      zone.esiLevels.includes(esiLevel)
    );
    
    // Sort by priority (prefer specialized zones for critical patients)
    if (esiLevel <= 2) {
      suitableZones.sort((a, b) => {
        if (a.id === 'emergency' || a.id === 'icu') return -1;
        if (b.id === 'emergency' || b.id === 'icu') return 1;
        return 0;
      });
    }
    
    // Find first available bed in suitable zones
    for (const zone of suitableZones) {
      const availableBed = state.beds.find(bed => 
        bed.zone === zone.id && bed.status === BED_STATUS.AVAILABLE
      );
      if (availableBed) {
        return availableBed;
      }
    }
    
    return null;
  }, [state.beds]);
  
  const value = useMemo(() => ({
    // State
    beds: state.beds,
    zones: state.zones,
    alerts: state.alerts,
    bedsByZone,
    occupancyStats,
    loading: state.loading,
    error: state.error,
    isConnected: state.isConnected,
    connectionError: state.connectionError,
    lastUpdate,
    
    // Actions
    assignBed,
    releaseBed,
    updateBedStatus,
    transferPatient,
    findAvailableBed,
    
    // Constants
    BED_STATUS,
    BED_ZONES
  }), [
    state,
    bedsByZone,
    occupancyStats,
    lastUpdate,
    assignBed,
    releaseBed,
    updateBedStatus,
    transferPatient,
    findAvailableBed
  ]);
  
  return (
    <BedManagementContext.Provider value={value}>
      {children}
    </BedManagementContext.Provider>
  );
};

// Hook to use bed management context
export const useBedManagement = () => {
  const context = useContext(BedManagementContext);
  if (!context) {
    throw new Error('useBedManagement must be used within BedManagementProvider');
  }
  return context;
};

export default BedManagementContext; 